import { useActivePanel, type PanelType } from "@/hooks/use-active-panel";
import { useNavigate } from "react-router-dom";
import { Building2, ShieldCheck, Zap, ChevronsUpDown, Check , Store } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PANEL_CONFIG: Record<
  PanelType,
  { label: string; icon: typeof Building2; route: string; colorClass: string; bgClass: string }
> = {
  user: {
    label: "Cliente",
    icon: Building2,
    route: "/",
    colorClass: "text-emerald-500",
    bgClass: "bg-emerald-500/10",
  },
  partner: {
    label: "Anunciante",
    icon: Store,
    route: "/vitrine/meus-anuncios",
    colorClass: "text-amber-500",
    bgClass: "bg-amber-500/10",
  },
  admin: {
    label: "Admin",
    icon: ShieldCheck,
    route: "/admin",
    colorClass: "text-blue-500",
    bgClass: "bg-blue-500/10",
  },
  superadmin: {
    label: "Super",
    icon: Zap,
    route: "/superadmin",
    colorClass: "text-violet-500",
    bgClass: "bg-violet-500/10",
  },
};

interface ContextSwitcherProps {
  collapsed?: boolean;
  /**
   * Variante enxuta para o header mobile, onde não há sidebar para hospedar o
   * seletor. Some quando há um painel só: no celular o espaço do topo é
   * disputado, e rótulo estático sem escolha possível não informa nada.
   */
  compact?: boolean;
}

export function ContextSwitcher({ collapsed = false, compact = false }: ContextSwitcherProps) {
  const { activePanel, setActivePanel, availablePanels } = useActivePanel();
  const navigate = useNavigate();

  const current = PANEL_CONFIG[activePanel];
  const Icon = current.icon;

  function handleSwitch(panel: PanelType) {
    if (panel === activePanel) return;
    setActivePanel(panel);
    navigate(PANEL_CONFIG[panel].route);
  }

  function renderItems() {
    return availablePanels.map((panel) => {
      const { label, icon: PIcon, colorClass } = PANEL_CONFIG[panel];
      const isActive = panel === activePanel;
      return (
        <DropdownMenuItem
          key={panel}
          onClick={() => handleSwitch(panel)}
          className="flex items-center gap-2 cursor-pointer"
          aria-current={isActive ? "true" : undefined}
        >
          <PIcon className={cn("h-4 w-4", colorClass)} />
          <span className="flex-1">{label}</span>
          {isActive && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
        </DropdownMenuItem>
      );
    });
  }

  // Header mobile: ícone + rótulo, sem ocupar largura total
  if (compact) {
    if (availablePanels.length <= 1) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="context-switcher-compact"
            className="flex items-center gap-1.5 h-9 px-2 rounded-lg hover:bg-muted transition-colors shrink-0"
            aria-label="Trocar contexto de painel"
          >
            <div className={cn("h-6 w-6 rounded flex items-center justify-center shrink-0", current.bgClass)}>
              <Icon className={cn("h-3.5 w-3.5", current.colorClass)} />
            </div>
            {/* Ícone sozinho não diz que aquilo troca de perfil. O rótulo cabe
                em qualquer celular a partir de 360px — abaixo disso o header
                do painel Cliente (menu, logo, sino e avatar) transborda. */}
            <span className="hidden min-[360px]:inline text-[13px] font-semibold text-foreground">
              {current.label}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 z-50">
          {renderItems()}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Collapsed sidebar: ícones empilhados por painel disponível
  if (collapsed) {
    if (availablePanels.length <= 1) return null;
    return (
      <div className="flex flex-col gap-1 px-2 py-2">
        {availablePanels.map((panel) => {
          const { icon: PIcon, colorClass, bgClass, label } = PANEL_CONFIG[panel];
          const isActive = panel === activePanel;
          return (
            <button
              key={panel}
              onClick={() => handleSwitch(panel)}
              title={label}
              aria-label={`Mudar para contexto ${label}`}
              className={cn(
                "h-8 w-full rounded-md flex items-center justify-center transition-colors",
                isActive
                  ? `${bgClass} ${colorClass}`
                  : "text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              )}
            >
              <PIcon className="h-4 w-4" />
            </button>
          );
        })}
      </div>
    );
  }

  // Painel único: label estático sem dropdown
  if (availablePanels.length <= 1) {
    return (
      <div className="flex items-center gap-2 px-4 py-3">
        <div className={cn("h-6 w-6 rounded flex items-center justify-center shrink-0", current.bgClass)}>
          <Icon className={cn("h-3.5 w-3.5", current.colorClass)} />
        </div>
        <span className="text-[13px] font-semibold text-foreground">{current.label}</span>
      </div>
    );
  }

  // Multi-painel: dropdown estilo Vercel
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="context-switcher"
          className="flex items-center gap-2 px-3 py-2.5 w-full hover:bg-sidebar-accent/50 transition-colors rounded-lg mx-1 my-1"
          aria-label="Trocar contexto de painel"
        >
          <div className={cn("h-6 w-6 rounded flex items-center justify-center shrink-0", current.bgClass)}>
            <Icon className={cn("h-3.5 w-3.5", current.colorClass)} />
          </div>
          <span className="flex-1 text-left text-[13px] font-semibold text-foreground truncate">
            {current.label}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {renderItems()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
